// @flow
import type { ContextRouter } from "react-router";
import React from "react";
import { InView } from "react-intersection-observer";
import { connect } from "react-redux";
import styled from "styled-components";
import { Helmet } from "react-helmet";

import { Sentry } from "@nested/isomorphic-sentry";
import { formatPrice, getImage, PropertyTypeDescription } from "@nested/utils";
import { createAnalyticsAction } from "@nested/analytics";
import { HousePin } from "@nested/ui/icons";
import { media, regularHeading, mediumParagraph } from "@nested/brand";
import { ButtonLink } from "@nested/component-library";
import { PropertyCarousel } from "@nested/brand/src/slices/PropertyCarousel/PropertyCarousel";
import { NotFound } from "@nested/brand/src/pages/NotFound";

import { fetchPropertyListings } from "./ducks";
import { MapComponent } from "./components/MapComponent";
import { HeroSection } from "./components/HeroSection";
import { FloatingEnquiryBox } from "./components/FloatingEnquiryBox";
import { KeyFeatures } from "./components/KeyFeatures";
import { Floorplan } from "./components/Floorplan";

const H3 = styled.h3`
  ${regularHeading}
  margin-top: 10px;
`;

const BottomNav = styled.div`
  position: ${({ sticky }) => (sticky ? "fixed" : "relative")};
  bottom: 0;
  background-color: white;
  height: 68px;
  width: 100vw;
  display: flex;
  align-items: center;
  justify-content: space-evenly;
  box-shadow: 0 -1px 5px 0 rgba(34, 34, 34, 0.1);
  ${media.tablet`
    display: none;
  `};
`;

const ContactButton = styled(ButtonLink)`
  box-sizing: border-box;
  text-align: center;
  width: 46%;
  margin: 10px 0;
  box-shadow: 0 2px 4px 0 rgba(34, 34, 34, 0.1);
`;

const Text = styled.div`
  ${mediumParagraph}
  margin-top: 20px;
  white-space: pre-wrap;
`;

const MaterialInfoField = styled.div`
  ${mediumParagraph}
  font-style: italic;
`;

const MaterialInfoWrapper = styled.div`
  margin-top: 10px;
`;

const DescriptionSectionWrapper = styled.div`
  box-sizing: border-box;
  width: 100%;
  max-width: 1270px;
  padding: 0 20px 50px;
  display: flex;
  ${media.tablet`
    margin: 0 auto;
  `};
  ${media.desktop`
    padding-left: 0;
  `}
`;

const DescriptionSection = styled.div`
  width: 100%;

  > p {
    max-width: 590px;
  }

  h3 {
    margin-bottom: 10px;
    text-align: left;
  }

  ${media.tablet`
    margin: 0 auto 0 0;
  `};
`;

const SideBySideWrapper = styled.div`
  box-sizing: border-box;
  width: 100%;
  display: flex;
  flex-direction: column;

  ${media.desktop`
    margin-top: 80px;
    flex-direction: row-reverse;
    & > div {
      width: 50%;
    }
  `}
`;

const ContentWrapper = styled.div`
  box-sizing: border-box;
  width: 100%;
  max-width: 1180px;
  margin: 0 auto;
  ${media.desktop`
    padding: 0 20px;
  `};
`;

export const formatAddress = (address: PropertyAddress) =>
  [address?.street, address?.borough, address?.outcode]
    .filter((string) => string)
    .join(", ");

type GTMData = {
  propertyId: string,
  price: ?number,
  bedrooms: ?number,
  status: PropertyStatus,
  propertyType: ?string,
  address: PropertyAddress,
};

type OuterProps = {|
  ...ContextRouter<{ dealExternalId: string }>,
|};

type Props = {|
  ...OuterProps,
  pushToGTM: (propertyDetails: GTMData) => void,
  listingConfig: FullPropertyListingDatum,
  fetchedListingConfig: boolean,
|};

type State = {
  fixedBottomNavigation: boolean,
};

type MaterialInformationProps = {
  tenure?: ?string,
  leaseLength?: ?number,
  serviceCharge?: ?string,
  groundRent?: ?string,
  councilTaxBand?: ?string,
};

export class IndividualPropertyPageComponent extends React.Component<
  Props,
  State,
> {
  bottomNav = React.createRef<InView>();

  bottomSpace = React.createRef<InView>();

  constructor(props: Props) {
    super(props);
    this.state = { fixedBottomNavigation: true };
  }

  componentDidMount() {
    const {
      match: {
        params: { dealExternalId },
      },
      listingConfig,
      fetchedListingConfig,
      pushToGTM,
    } = this.props;

    if (listingConfig) {
      const { price, bedrooms, status, propertyType, address } = listingConfig;
      return pushToGTM({
        propertyId: dealExternalId,
        price,
        bedrooms,
        status,
        propertyType,
        address,
      });
    }
    if (fetchedListingConfig) {
      // if the fetch fails, it must be an invalid ID
      return Sentry.captureException(
        new Error(`No property listing found for ID: ${dealExternalId}`),
      );
    }
    return null;
  }

  componentDidUpdate() {
    const {
      match: {
        params: { dealExternalId },
      },
      listingConfig,
      pushToGTM,
    } = this.props;
    if (listingConfig) {
      const { price, bedrooms, status, propertyType, address } = listingConfig;

      pushToGTM({
        propertyId: dealExternalId,
        price,
        bedrooms,
        status,
        propertyType,
        address,
      });
    }
  }

  /*
   * I couldn't figure out a better way to make the bottom navigation of this
   * page sticky until the footer is in view, so I setup an observer to track
   * scrolling and update the navigation accordingly
   */
  updateBottomNavigation = (inView: boolean) => {
    if (this.bottomSpace.current && this.bottomSpace.current.node) {
      const domRect = this.bottomSpace.current.node.getBoundingClientRect();

      /*
       * if the top of the element is not visible when this function is triggered
       * then it means that user was scrolling down
       * therefore we don't want to make bottom navigation sticky
       */
      if (domRect.top < 0) {
        return;
      }
      this.setState({ fixedBottomNavigation: !inView });
    }
  };

  coordinates = (address: PropertyAddress): Coordinates => ({
    lat: Number(address?.lat),
    lng: Number(address?.lng),
  });

  static fetchData = fetchPropertyListings;

  render() {
    const { listingConfig, fetchedListingConfig, location } = this.props;
    const { fixedBottomNavigation } = this.state;

    const invalidId = fetchedListingConfig && !listingConfig;

    const stillLoading = !fetchedListingConfig;

    if (invalidId) {
      return <NotFound />;
    }

    if (stillLoading) {
      return null;
    }

    const {
      externalId,
      images,
      address,
      price,
      priceQualifier,
      status,
      propertyType,
      bedrooms,
      keyFeatures,
      floorPlanImageUrl,
      description,
      rightmoveLink,
      epcImageUrl,
      tenure,
      leaseLength,
      serviceCharge,
      groundRent,
      councilTaxBand,
    } = listingConfig;

    const formattedAddress = formatAddress(address);

    const metaDescription = `${bedrooms} bedroom ${PropertyTypeDescription(
      propertyType,
    )} for sale in ${formattedAddress}.`;

    const title = `${formattedAddress} - Nested`;

    const addressCoordinates = this.coordinates(address);

    return (
      <div data-test="page:individual-property-page">
        <Helmet>
          <title>{title}</title>
          <meta name="description" content={metaDescription} />
          <meta
            property="og:title"
            content="Check out this property for sale on Nested.com!"
          />
          <meta property="og:type" content="article" />
          <meta property="og:description" content={metaDescription} />
          <meta
            property="og:image"
            content={getImage(`property-listings/${externalId}/${images[0]}`)}
          />
          <meta
            property="og:url"
            content={`https://nested.com${location.pathname}`}
          />
        </Helmet>

        <HeroSection
          status={status}
          price={price}
          priceQualifier={priceQualifier}
          propertyType={propertyType}
          bedrooms={bedrooms}
          formattedAddress={formattedAddress}
          images={images}
          externalId={externalId}
          tenure={tenure ? tenure.replace(/_/g, " ") : null}
          leaseLength={leaseLength}
          serviceCharge={serviceCharge}
          groundRent={groundRent}
          councilTaxBand={councilTaxBand}
        />

        <ContentWrapper>
          <SideBySideWrapper>
            <MapComponent center={addressCoordinates}>
              <HousePin
                style={{ marginTop: "-64px", marginLeft: "-32px" }}
                width="64px"
                height="64px"
                {...addressCoordinates}
                fillStyle={{
                  background: "#0A4254",
                  colour: "#FF8770",
                }}
              />
            </MapComponent>

            <KeyFeatures
              keyFeatures={keyFeatures}
              rightmoveLink={rightmoveLink}
            />
          </SideBySideWrapper>

          <SideBySideWrapper>
            <Floorplan
              externalId={externalId}
              floorPlanImageUrl={floorPlanImageUrl}
              epcImageUrl={epcImageUrl}
            />

            <DescriptionSectionWrapper>
              <DescriptionSection>
                <H3>Full description</H3>
                <Text dangerouslySetInnerHTML={{ __html: description }} />
                <MaterialInformation
                  tenure={tenure}
                  leaseLength={leaseLength}
                  serviceCharge={serviceCharge}
                  groundRent={groundRent}
                  councilTaxBand={councilTaxBand}
                />
              </DescriptionSection>
            </DescriptionSectionWrapper>
          </SideBySideWrapper>
        </ContentWrapper>

        <PropertyCarousel
          buttonLabel="See all homes"
          heading="See more homes from sellers around the UK"
        />

        <InView ref={this.bottomSpace} onChange={this.updateBottomNavigation}>
          <div />
        </InView>

        <FloatingEnquiryBox location={location} />

        <BottomNav sticky={fixedBottomNavigation}>
          <ContactButton to="tel:+442038580695">Call us</ContactButton>
        </BottomNav>
      </div>
    );
  }
}

const MaterialInformation = ({
  tenure,
  leaseLength,
  serviceCharge,
  groundRent,
  councilTaxBand,
}: MaterialInformationProps) => {
  const showLeaseInfo = !!(
    tenure === "LEASEHOLD" || tenure === "SHARE_OF_FREEHOLD"
  );

  const capitaliseFirstCharacterOnly = (str) => {
    if (str === "SHARE_OF_FREEHOLD") {
      return "Share of Freehold";
    }
    return str[0].toUpperCase() + str.slice(1).toLowerCase();
  };

  return (
    <>
      <MaterialInfoWrapper>
        <MaterialInfoField>
          Tenure:{" "}
          {tenure ? `${capitaliseFirstCharacterOnly(tenure)}` : "Ask agent"}
        </MaterialInfoField>
        {showLeaseInfo && (
          <>
            <MaterialInfoField>
              Lease Length:{" "}
              {leaseLength ? `${leaseLength} years remaining` : "Ask agent"}
            </MaterialInfoField>
            <MaterialInfoField>
              Service Charge:{" "}
              {serviceCharge
                ? `${formatPrice(Number(serviceCharge))} per year`
                : "Ask agent"}
            </MaterialInfoField>
            <MaterialInfoField>
              Ground Rent:{" "}
              {groundRent
                ? `${formatPrice(Number(groundRent))} per year`
                : "Ask agent"}
            </MaterialInfoField>
          </>
        )}
        <MaterialInfoField>
          Council Tax Band: {councilTaxBand ? `${councilTaxBand}` : "Ask agent"}
        </MaterialInfoField>
      </MaterialInfoWrapper>
    </>
  );
};

export const mapStateToProps = (state: ReduxStore) => ({
  listingConfig: state.propertyListing.listingConfig,
  fetchedListingConfig: state.propertyListing.fetched,
});

function mapDispatchToProps(dispatch: Dispatch) {
  return {
    pushToGTM(propertyDetails: GTMData) {
      const analyticsAction = createAnalyticsAction({
        ...propertyDetails,
        event: "forSalePageViewProperty",
      });

      dispatch(analyticsAction);
    },
  };
}

export const IndividualPropertyPage = connect<Props, OuterProps, _, _, _, _>(
  mapStateToProps,
  mapDispatchToProps,
)(IndividualPropertyPageComponent);

export const UniversalRender = IndividualPropertyPage;
